import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Stack,
  Divider,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { loginGuest, getGuestStorageKey } from './nexastayApi';

export default function NexaStayLoginDialog({ open, onClose, hotelId, onLoginSuccess }) {
  const theme = useNexaStayTheme();
  const accent = theme.accent;

  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!hotelId) {
      setErrors({ non_field_errors: ['Hotel not found.'] });
      return;
    }
    setIsSubmitting(true);
    try {
      const data = await loginGuest(hotelId, {
        email_or_phone: emailOrPhone.trim(),
        password,
      });
      if (data?.guest_id != null) {
        try {
          localStorage.setItem(getGuestStorageKey(hotelId), String(data.guest_id));
        } catch (_) {}
        onLoginSuccess?.(String(data.guest_id));
        onClose?.();
        return;
      }
      setErrors(data?.errors || { non_field_errors: ['Login failed.'] });
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) setErrors(data.errors);
      else setErrors({ non_field_errors: ['Invalid email/phone or password.'] });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueWithGoogle = () => {
    if (!hotelId) return;
    // Preserve query string (check_in, check_out, rooms_json, etc.) so OAuth return lands on the same checkout context.
    const next =
      typeof window !== 'undefined'
        ? encodeURIComponent(
            `${window.location.origin}${window.location.pathname}${window.location.search || ''}`
          )
        : '';
    const url = `${window.location.origin}/nexastay/login/google/?hotel_id=${encodeURIComponent(hotelId)}&next=${next}`;
    window.location.href = url;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${accent}40`,
          boxShadow:
            theme.mode === 'dark'
              ? `0 24px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06), 0 0 40px ${accent}20`
              : `0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06), 0 0 40px ${accent}15`,
          bgcolor: theme.paper,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: `${accent}18`,
          borderBottom: `2px solid ${accent}40`,
          px: 3,
          pt: 2.5,
          pb: 2,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: theme.textSecondary,
            '&:hover': { color: theme.textPrimary, bgcolor: `${accent}18` },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}30`,
              color: accent,
              boxShadow: `0 4px 12px ${accent}40`,
            }}
          >
            <LoginIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 700, letterSpacing: '-0.02em' }}>
              Welcome back
            </Typography>
            <Typography variant="body2" sx={{ color: theme.textSecondary }}>
              Sign in with your email or phone
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            {errors.non_field_errors?.length > 0 && (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                {errors.non_field_errors[0]}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Email or phone"
              type="text"
              value={emailOrPhone}
              onChange={(e) => setEmailOrPhone(e.target.value)}
              variant="outlined"
              autoComplete="username"
              error={!!errors.email_or_phone}
              helperText={errors.email_or_phone?.[0]}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)',
                  '& fieldset': { borderColor: `${accent}40` },
                  '&:hover fieldset': { borderColor: `${accent}80` },
                  '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 2 },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: accent },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              autoComplete="current-password"
              error={!!errors.password}
              helperText={errors.password?.[0]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      sx={{ color: theme.textSecondary }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)',
                  '& fieldset': { borderColor: `${accent}40` },
                  '&:hover fieldset': { borderColor: `${accent}80` },
                  '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 2 },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: accent },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting}
              endIcon={<LoginIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                bgcolor: accent,
                color: theme.mode === 'dark' ? '#121212' : '#000',
                boxShadow: `0 4px 16px ${accent}50`,
                border: `1px solid ${accent}66`,
                '&:hover': {
                  bgcolor: accent,
                  boxShadow: `0 6px 20px ${accent}60`,
                },
              }}
            >
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </form>

        {hotelId && (
          <>
            <Box sx={{ my: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Divider sx={{ flex: 1, borderColor: theme.border }} />
              <Typography variant="caption" sx={{ color: theme.textMuted, fontWeight: 500 }}>
                Or continue with
              </Typography>
              <Divider sx={{ flex: 1, borderColor: theme.border }} />
            </Box>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleContinueWithGoogle}
              startIcon={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                </Box>
              }
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                borderColor: theme.border,
                color: theme.textPrimary,
                '&:hover': {
                  borderColor: accent,
                  bgcolor: `${accent}12`,
                  color: accent,
                },
              }}
            >
              Continue with Google
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
